<template>
  <div v-if="filteredServices && filteredServices.length > 0">
    
    <div ref="containerRef" class="container faqlist-carousel" >
      
      <h2 class="text-3xl text-center mb-6">
        FAQ's
      </h2>

      <Carousel
        :items-to-show="carouselSlidesDisplayed"
        :items-to-scroll="carouselSlidesDisplayed"
        :wrapAround="false"
        snap-align="start"
        @slide-end="setEqualHeight"
      >
        <Slide v-for="service in filteredServices" :key="service.FAQId">
          <div class="faq-card" :ref="setFaqCardRef">
            <div class="p-6 h-full flex flex-col justify-between">
              <div>
                <h3 class="text-xl font-semibold mb-4 faq-questions">
                  {{ service.Question }}
                </h3>
                <p class="text-base faq-answer">
                  {{ service.Answer }}
                </p>
              </div>

              <div class="read-more">
                <NuxtLink :to=service.LearnMoreURL v-if="service.LearnMoreURL">
                  Read More >
                </NuxtLink>
              </div>
            </div>
          </div>
        </Slide>

        <template #addons>
          <Navigation />
        </template>
      </Carousel>

    </div>
    
  </div>
</template>

<script setup lang="ts">
import { Carousel, Slide, Navigation } from 'vue3-carousel'
import { useHead } from '@unhead/vue'
import store from '~/store'

const { $companies } = useNuxtApp()

const props = defineProps({
	categoryId: {
		type: Number,
		required: false
	},
  uP_ServiceDefinition_IDs: {
    type: Array as () => number[],
    required: false
  },
  /** When true, the component will not inject its own FAQPage schema (parent page handles it). */
  disableSchema: {
    type: Boolean,
    default: false,
  },
});

/* ---------------- REFS ---------------- */
const containerRef = ref<HTMLElement | null>(null)
const faqCards = ref<HTMLElement[]>([])
const containerWidth = ref(0)
const filteredServices = ref<any[]>([])
const maxCardHeight = ref(0)

/* -------- FUNCTION REF (IMPORTANT) -------- */
const setFaqCardRef = (el: HTMLElement | null) => {
  if (el && !faqCards.value.includes(el)) {
    faqCards.value.push(el)
  }
}

/* ---------------- API ---------------- */
const getAdminFAQs = async () => {
  try {
    let categoryId = props.categoryId || 0;
    let sqldbid = store.state.persistedState?.sqldbid || 0;
    let uP_ServiceDefinition_IDs = props.uP_ServiceDefinition_IDs || []

    const data = await $companies.getUCMSAdminFAQ({
      categoryId: categoryId,
      sqldbid: sqldbid,
      uP_ServiceDefinition_IDs: uP_ServiceDefinition_IDs
    });
    
    if (!data?.error) {
      filteredServices.value = data || []
    }

  } catch (e) {
    console.error('FAQ fetch failed:', e)
  }
}

/* ------------- WATCHERS ------------- */
// Refetch FAQs whenever the driving props change (covers SPA param changes)
watch(
  () => ({
    categoryId: props.categoryId,
    serviceIds: props.uP_ServiceDefinition_IDs ? [...props.uP_ServiceDefinition_IDs] : [],
  }),
  async () => {
    await getAdminFAQs()
  },
  { deep: true, immediate: true }
);

watch(filteredServices, async () => {
  faqCards.value = [] // reset refs
  await nextTick()    // wait for DOM to render the new cards
  updateContainerWidth()
  window.addEventListener('resize', updateContainerWidth)
  setEqualHeight()
});

/* ------------- HEIGHT LOGIC ------------- */
const setEqualHeight = async () => {
  await nextTick()
  if (!faqCards.value.length) return

  faqCards.value.forEach(card => (card.style.height = 'auto'))

  maxCardHeight.value = Math.max(
    ...faqCards.value.map(card => card.offsetHeight)
  )

  faqCards.value.forEach(card => { 
    card.style.height = `${maxCardHeight.value}px`
  })
}

/* ------------- RESPONSIVE ------------- */
const updateContainerWidth = () => {
  if (!containerRef.value) return
  containerWidth.value = containerRef.value.offsetWidth
  setEqualHeight()
}

const carouselSlidesDisplayed = computed(() => {
  if (containerWidth.value >= 800) return 3
  if (containerWidth.value >= 550) return 2
  return 1
})

/* ------------- LIFECYCLE ------------- */
onMounted(async () => {
  updateContainerWidth()
  window.addEventListener('resize', updateContainerWidth)
  setEqualHeight()
})

onUnmounted(() => {
  window.removeEventListener('resize', updateContainerWidth)
})

watch(filteredServices, () => {
  faqCards.value = [] // reset refs
  setEqualHeight()
})

/* ------------- SEO SCHEMA ------------- */
useHead(() => {
  if (props.disableSchema) return { script: [] }
  return {
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: filteredServices.value.map(item => ({
            '@type': 'Question',
            name: item.Question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.Answer
            }
          }))
        })
      }
    ]
  }
})
</script>

<style scoped>
.carousel__slide {
  padding: 0 10px;
}

.faq-card {
  background: #EEF1F4;
  border-radius: 30px;
  transition: height 0.2s ease;
}

.faq-questions {
  color: #00810e;
}

.read-more {
  color: #00810e;
  font-weight: 600;
  text-align: right;
  cursor: pointer;
}
</style>
